/**
 * WEB-1204 — keep one-time auth secrets out of PostHog.
 *
 * Magic-link, verify-email, and reset-password links put the secret in
 * `?token=` (aliases `code` and `t` are stripped too). PostHog stores
 * `window.location.href` as `$current_url` on the manual pageview, on
 * autocapture, and on `$pageleave`. These helpers are the shared strip.
 *
 * `#t=` fragments are intentionally left alone: moving the secret into the
 * fragment needs the email templates and the API, which is a follow-up.
 */

/** Query keys that carry a one-time auth secret. Matched case-insensitively. */
export const AUTH_SECRET_QUERY_KEYS: readonly string[] = ['token', 'code', 't'];

/**
 * Paths whose DOM must not be session-replayed. Replay is not turned on in
 * `posthog.init` today; the exclusion still runs so a later project-level
 * enable cannot record these screens. posthog-js reads URL blocklists from
 * remote config only (`sessionRecording.urlBlocklist`), not from
 * `session_recording` init options, so the client stops and resumes recording
 * around these paths instead.
 */
export const SESSION_REPLAY_EXCLUDED_PATHS: readonly string[] = [
  '/magic-link',
  '/verify-email',
  '/reset-password',
];

const AUTH_SECRET_QUERY_KEY_SET: ReadonlySet<string> = new Set(AUTH_SECRET_QUERY_KEYS);

/** Fast reject for strings that cannot carry one of the secret query keys. */
const AUTH_SECRET_QUERY_PATTERN = /[?&#](?:token|code|t)=/i;

const RELATIVE_URL_BASE = 'https://loci.invalid';

export function isAuthSecretQueryKey(key: string): boolean {
  return AUTH_SECRET_QUERY_KEY_SET.has(key.toLowerCase());
}

export function isSessionReplayExcludedPath(pathname: string): boolean {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return SESSION_REPLAY_EXCLUDED_PATHS.includes(normalized);
}

/**
 * Remove `token`, `code`, and `t` query params from an absolute or relative
 * URL. Other params, including `ticket` (agent-login), are preserved. The
 * fragment is preserved, so `#t=` is not stripped.
 *
 * Returns the same string when nothing changes, so callers can compare by
 * identity.
 */
export function stripAuthSecretsFromUrl(raw: string): string {
  if (!AUTH_SECRET_QUERY_PATTERN.test(raw)) return raw;
  return stripQueryParams(raw, (key) => isAuthSecretQueryKey(key));
}

/**
 * Query keys removed from every URL-shaped string PostHog is sent (WEB-1229),
 * on top of {@link AUTH_SECRET_QUERY_KEYS}. Capture-only: the address bar
 * keeps them, because a page such as `/verify-email?email=` still reads its
 * own params. `q` is free text and may hold personal data; the Discover
 * filter params (`category`, `date`, `neighborhood`, `price`, `view`,
 * `sort`, …) are kept.
 */
export const ANALYTICS_REDACTED_QUERY_KEYS: readonly string[] = [
  'email',
  'access_token',
  'refresh_token',
  'id_token',
  'password',
  'otp',
  'state',
  'phone',
  'q',
];

const ANALYTICS_REDACTED_QUERY_KEY_SET: ReadonlySet<string> = new Set([
  ...AUTH_SECRET_QUERY_KEYS,
  ...ANALYTICS_REDACTED_QUERY_KEYS,
]);

/** Fast reject: no redacted key and no `@` / `%40` in any query value. */
const ANALYTICS_REDACTED_QUERY_PATTERN = new RegExp(
  `[?&#](?:${[...ANALYTICS_REDACTED_QUERY_KEY_SET].join('|')})=|[?&][^=&#]*=[^&#]*(?:@|%40)`,
  'i',
);

/** An address in a query value, plain or percent-encoded. */
const EMAIL_IN_VALUE_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;

/**
 * Remove the auth secrets, the {@link ANALYTICS_REDACTED_QUERY_KEYS}, and any
 * param whose value holds an email address. Used by the `before_send`
 * backstop, so it applies to `$current_url` and every other URL property.
 * Same identity contract as {@link stripAuthSecretsFromUrl}.
 */
export function sanitizeAnalyticsUrl(raw: string): string {
  if (!ANALYTICS_REDACTED_QUERY_PATTERN.test(raw)) return raw;
  return stripQueryParams(
    raw,
    (key, value) =>
      ANALYTICS_REDACTED_QUERY_KEY_SET.has(key.toLowerCase()) || EMAIL_IN_VALUE_PATTERN.test(value),
  );
}

function stripQueryParams(
  raw: string,
  shouldStrip: (key: string, value: string) => boolean,
): string {
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw);
  let url: URL;
  try {
    url = hasScheme ? new URL(raw) : new URL(raw, RELATIVE_URL_BASE);
  } catch {
    return raw;
  }

  const entries = [...url.searchParams.entries()];
  const kept = entries.filter(([key, value]) => !shouldStrip(key, value));
  if (kept.length === entries.length) return raw;
  url.search = new URLSearchParams(kept).toString();

  if (hasScheme) return url.toString();
  if (raw.startsWith('/')) return `${url.pathname}${url.search}${url.hash}`;
  if (raw.startsWith('?')) return `${url.search}${url.hash}`;
  return raw;
}

/**
 * History state safe to hand to `replaceState` on an App Router page.
 *
 * Next 16 patches `replaceState` and, when the state already has `__NA` or
 * `_N`, writes the URL and skips `ACTION_RESTORE`. `HistoryUpdater` stamps
 * `__NA` on every commit, so passing `window.history.state` through leaves
 * the router's canonical URL on the token link and a later update puts the
 * secret back in the address bar. Drop only those markers. `null` would also
 * skip the bail-out, and it throws away custom state (`idx`, and anything
 * else the entry was carrying).
 */
function historyStateWithoutNextMarkers(state: unknown): unknown {
  if (state === null || typeof state !== 'object' || Array.isArray(state)) return state;
  const record = state as Record<string, unknown>;
  if (!('__NA' in record) && !('_N' in record)) return state;
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (key === '__NA' || key === '_N') continue;
    next[key] = value;
  }
  return next;
}

/**
 * Drop secret query params from the address bar. Synchronous, so a
 * `useLayoutEffect` caller finishes before any `useEffect` (including the
 * analytics pageview) reads `location.href`.
 */
export function stripAuthSecretsFromBrowserLocation(): void {
  if (typeof window === 'undefined') return;
  const next = stripAuthSecretsFromUrl(window.location.href);
  if (next === window.location.href) return;
  const stripped = new URL(next);
  const relative = `${stripped.pathname}${stripped.search}${stripped.hash}`;
  window.history.replaceState(historyStateWithoutNextMarkers(window.history.state), '', relative);
}

/**
 * How many times an auth page has finished stripping this document.
 * `PageViewTracker` is outside the Suspense boundary around `useSearchParams`,
 * so its effect can run while the token page is still on the fallback and
 * the address bar still has the secret. It holds the pageview until this
 * epoch moves.
 */
let authSecretStripEpoch = 0;
const authSecretStripListeners = new Set<() => void>();

export function currentAuthSecretStripEpoch(): number {
  return authSecretStripEpoch;
}

/** Subscribe first, then read {@link currentAuthSecretStripEpoch}. */
export function subscribeAuthSecretStrip(listener: () => void): () => void {
  authSecretStripListeners.add(listener);
  return () => {
    authSecretStripListeners.delete(listener);
  };
}

/** Call after `stripAuthSecretsFromBrowserLocation`, including when the URL was already clean. */
export function notifyAuthSecretsStripped(): void {
  authSecretStripEpoch += 1;
  for (const listener of authSecretStripListeners) listener();
}

/** Test seam. The epoch is process-local and would otherwise leak across cases. */
export function resetAuthSecretStripForTests(): void {
  authSecretStripEpoch = 0;
  authSecretStripListeners.clear();
}

function scrubUnknown(value: unknown, depth: number): unknown {
  if (depth > 6) return value;
  if (typeof value === 'string') return sanitizeAnalyticsUrl(value);
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const scrubbed = scrubUnknown(item, depth + 1);
      if (scrubbed !== item) changed = true;
      return scrubbed;
    });
    return changed ? next : value;
  }
  if (value !== null && typeof value === 'object') {
    if (value instanceof Date) return value;
    const record = value as Record<string, unknown>;
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(record)) {
      const scrubbed = scrubUnknown(child, depth + 1);
      next[key] = scrubbed;
      if (scrubbed !== child) changed = true;
    }
    return changed ? next : value;
  }
  return value;
}

/**
 * Minimal capture shape accepted by posthog-js `before_send`. Structural so
 * this module does not import the SDK (the provider lazy-loads it).
 */
export interface AuthSecretCaptureEvent {
  uuid: string;
  event: string;
  properties: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
  timestamp?: Date;
}

/**
 * `before_send` backstop. Strips secret and personal-data query params
 * ({@link sanitizeAnalyticsUrl}) from `$current_url`, `$referrer`,
 * `$initial_current_url`, and any other string property that carries the
 * same URL shape (including `$set` / `$set_once`). Returns the same object
 * when nothing matched.
 */
export function scrubAuthSecretsFromCapture(
  event: AuthSecretCaptureEvent | null,
): AuthSecretCaptureEvent | null {
  if (!event) return event;
  const properties = scrubUnknown(event.properties, 0) as AuthSecretCaptureEvent['properties'];
  const set =
    event.$set === undefined
      ? undefined
      : (scrubUnknown(event.$set, 0) as AuthSecretCaptureEvent['$set']);
  const setOnce =
    event.$set_once === undefined
      ? undefined
      : (scrubUnknown(event.$set_once, 0) as AuthSecretCaptureEvent['$set_once']);
  if (properties === event.properties && set === event.$set && setOnce === event.$set_once) {
    return event;
  }
  return {
    ...event,
    properties,
    ...(set !== undefined ? { $set: set } : {}),
    ...(setOnce !== undefined ? { $set_once: setOnce } : {}),
  };
}

export interface SessionReplayControl {
  stopSessionRecording?: () => void;
  startSessionRecording?: () => void;
}

/**
 * True after this page load has stopped replay for an excluded path. The next
 * non-excluded path resumes it. Normal navigations that never touch those
 * paths do not call into the recorder.
 */
let replayStoppedForAuthPath = false;

/** Test seam. The flag is process-local and would otherwise leak across cases. */
export function resetSessionReplayPathSync(): void {
  replayStoppedForAuthPath = false;
}

export function syncSessionReplayForPath(client: SessionReplayControl, pathname: string): void {
  if (isSessionReplayExcludedPath(pathname)) {
    replayStoppedForAuthPath = true;
    client.stopSessionRecording?.();
    return;
  }
  if (!replayStoppedForAuthPath) return;
  replayStoppedForAuthPath = false;
  client.startSessionRecording?.();
}
